import React, { useEffect, useState } from 'react';

import { faWindowClose } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Subscription, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

import { useUnmount } from '@hooks';
import FEATURE_FLAGS from 'components/CRM/Common/featureFlags';
import { getIsFeatureFlagActive } from 'components/CRM/Common/hooks/useIsFeatureFlagActive';
import { FeatureFlag } from 'interfaces/FeatureFlag';
import { User } from 'interfaces/User';

import {
    talkdeskFactory as factory,
    talkdeskMessenger as messenger,
    talkdeskValidator as validator,
} from '../../components/Common/TalkdeskUtils';
import { currentUserService } from '../../subjects/common/CurrentUserService';
import { configurationService } from '../../subjects/crossModules/ConfigurationService';
import { talkdeskCtiService } from '../../subjects/crossModules/TalkdeskCtiService';
import { talkdeskInboundService } from '../../subjects/crossModules/TalkdeskInboundService';
import { talkdeskOutboundService } from '../../subjects/crossModules/TalkdeskOutboundService';

interface TalkdeskProps {
    crmFeatureFlags: FeatureFlag[] | null;
}

export default function Talkdesk({ crmFeatureFlags }: TalkdeskProps) {
    const unmountSub = useUnmount();

    const isCtiEnabled = getIsFeatureFlagActive(crmFeatureFlags, FEATURE_FLAGS.TALKDESK_INTEGRATION);
    const isInboundCallsEnabled = getIsFeatureFlagActive(crmFeatureFlags, FEATURE_FLAGS.TALKDESK_INBOUND_CALLS);
    const isOutboundCallsEnabled = getIsFeatureFlagActive(crmFeatureFlags, FEATURE_FLAGS.TALKDESK_OUTBOUND_CALLS);
    const isLoginRemindersEnabled = getIsFeatureFlagActive(crmFeatureFlags, FEATURE_FLAGS.TALKDESK_LOGIN_REMINDERS);

    const [ctiUrl, setCtiUrl] = useState('');
    const [userId, setUserId] = useState('');
    const [userEnabled, setUserEnabled] = useState(false);
    const [visible, setVisible] = React.useState(true);

    // subscriptions
    function getIframeSrcSubscription(): Subscription {
        return unmountSub(configurationService.getAppSetting$('TalkdeskCTISource'), (response) => {
            setCtiUrl(response.response);
        });
    }
    function getCurrentUserSubscription(): Subscription {
        return unmountSub(currentUserService.get(), (user) => {
            const u = user as User;
            setUserId(u.userId);
            setUserEnabled(u.talkdeskEnabled);
        });
    }
    function getIframeToggleSubscription(): Subscription {
        return unmountSub(talkdeskCtiService.iframeToggleMessages$(), () => {
            setVisible(!visible);
        });
    }
    function getGlobalMessageEventsSubscription(): Subscription {
        // this will emit every talkdesk automated task (auth, inbound started/ended, outbound started/ended)
        return unmountSub(
            fromEvent(window, 'message').pipe(
                filter(validator.isMessageEvent),
                filter(validator.isFromTalkdesk),
                // , tap(e => validator.logEvent(e.data.action, e.data))
            ),
            (event) => {
                talkdeskCtiService.ctiMessages.next(event);

                if (isInboundCallsEnabled) {
                    talkdeskInboundService.inboundCallEndedMessages.next(event);
                    talkdeskInboundService.inboundCallStartedMessages.next(event);
                }
                if (isOutboundCallsEnabled) {
                    talkdeskOutboundService.outboundCallEndedMessages.next(event);
                    talkdeskOutboundService.outboundCallStartedMessages.next(event);
                }
                if (isLoginRemindersEnabled) {
                    talkdeskCtiService.userSessionMessages.next(event);
                }
            },
        );
    }
    function getAuthenticationRequestSubscription(): Subscription {
        return unmountSub(talkdeskCtiService.externalIdMessages$(), (_) => {
            setVisible(false);
            messenger.sendMessageEvent(window, factory.createGetExternalIdMessage(userId));
        });
    }
    function getClickToCallRequestsSubscription(): Subscription {
        return unmountSub(talkdeskOutboundService.outboundCallRequestedMessages$(), (outboundCallData) => {
            messenger.sendMessageEvent(window, factory.createStartOutboundCallMessage(userId, outboundCallData));
        });
    }

    // handle subscriptions
    useEffect(() => {
        // load iframe, subscribe to:
        if (isCtiEnabled) {
            // get current user to: check if it has access to talkdesk & get its externalId
            getCurrentUserSubscription();
            // handle toggle events from the navigation menu
            getIframeToggleSubscription();
            talkdeskInboundService.initialize();
        }
    }, [isCtiEnabled, visible]);

    useEffect(() => {
        if (isCtiEnabled && userEnabled) {
            // get the iframe URL from appsettings
            getIframeSrcSubscription();
            // observe authentication requests & talkdesk automated tasks (if enabled)
            getGlobalMessageEventsSubscription();
            // handle authentication requests (send current user's externalId to talkdesk)
            getAuthenticationRequestSubscription();
            // handle AVA's click to call request
            if (isOutboundCallsEnabled) {
                // send the request to talkdesk (this will launch the call in the callbar)
                getClickToCallRequestsSubscription();
            }
            if (isLoginRemindersEnabled) {
                talkdeskCtiService.initLoginReminders();
            }
        }
    }, [isCtiEnabled, isOutboundCallsEnabled, isInboundCallsEnabled, isLoginRemindersEnabled, userId, userEnabled]);

    // HTML elements & behavior
    if (isCtiEnabled && userEnabled)
        return (
            <div className={visible ? 'talkdesk-container talkdesk-open' : 'talkdesk-container talkdesk-closed'}>
                <span className={'talkdesk-close-button'}>
                    <FontAwesomeIcon
                        icon={faWindowClose}
                        size='lg'
                        onClick={(_) => setVisible(false)}
                    />
                </span>
                <iframe
                    id='talkdesk_cti'
                    src={ctiUrl}
                    className={'talkdesk-cti'}
                ></iframe>
            </div>
        );
    else return <></>;
}
